import { Color, Spacing } from '@persona-id/design-tokens';
import { DateTime } from 'luxon';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import AttributeItem from 'dashboard/components/AttributeItem';
import CopyableField from 'dashboard/components/CopyableField';
import EventNameSticker from 'dashboard/components/EventNameSticker';
import NavBack from 'dashboard/components/NavBack';
import Permissioned from 'dashboard/components/Permissioned';
import Pii from 'dashboard/components/Pii';
import { RoutedTabbedSection } from 'dashboard/components/TabbedSectionV2';
import { MonoSpan } from 'dashboard/components/Typography';
import WebhookEventStatusSticker from 'dashboard/components/WebhookEventStatusSticker';
import CodeHighlight from 'dashboard/components-lib/CodeHighlight';
import { FormPasswordConfirmationModalData } from 'dashboard/forms/FormPasswordConfirmationModal';
import { Container, Header } from 'dashboard/layouts/IndexViewLayoutV2';
import {
  Backdrop,
  ColumnContainer,
  InfoGrid,
  MainColumn,
  RightColumn,
} from 'dashboard/layouts/ShowViewLayoutV2';
import { Organization } from 'dashboard/models/organization';
import { WebhookEvent } from 'dashboard/models/webhook-event';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import Sticker from 'design-system/components/Sticker';
import { Heading, Paragraph } from 'design-system/components/Text';
import { DATE_AND_TIME_TZ_FORMAT } from 'lib/constants/luxonFormats';
import { Permission } from 'lib/permissions';

interface DashboardWebhookEventsShowViewProps {
  organization: Organization;
  webhookEvent: WebhookEvent;
  handleRerunWebhookEvent: (
    webhookEvent: WebhookEvent,
    meta?: FormPasswordConfirmationModalData,
  ) => Promise<void>;
}

const DashboardWebhookEventsShowView = ({
  webhookEvent,
  handleRerunWebhookEvent,
}: DashboardWebhookEventsShowViewProps) => {
  const location = useLocation();
  // TODO(after react-router v6 upgrade) replace `navBackLocation` with -1
  // https://stackoverflow.com/a/71447845/4951255
  const navBackLocation =
    location.state?.fromWebhookEventsView === true
      ? '/dashboard/webhook-events'
      : `/dashboard/webhooks/${webhookEvent.relationships.webhook.data.id}`;

  return (
    <>
      <Helmet title={`${webhookEvent.id} - Webhook Events`} />
      <Header prepend={<NavBack to={navBackLocation} />} title={`Webhook Event ${webhookEvent.id}`}>
        <HeaderRow>
          <HStack>
            <Permissioned permission={Permission.WebhookEventWrite}>
              <Button
                variant='basic'
                id='webhook-event__header__rerun'
                onClick={async () => {
                  await handleRerunWebhookEvent(webhookEvent);
                }}
              >
                Rerun
              </Button>
            </Permissioned>
          </HStack>
        </HeaderRow>
      </Header>
      <Backdrop>
        <Container>
          <ColumnContainer>
            <MainColumn>
              <RoutedTabbedSection
                dataTest='webhook-event__tabs'
                tabs={[
                  {
                    name: 'Request',
                    path: '',
                    contents: (
                      <>
                        <Card>
                          <Card.Body>
                            <Card.Title>Request</Card.Title>
                            <Pii isRedacted={false}>
                              <CodeHighlight
                                id='webhook-events__body'
                                language='json'
                                rowsToShow={40}
                              >
                                {JSON.stringify(
                                  JSON.parse(webhookEvent.attributes['body']),
                                  null,
                                  2,
                                )}
                              </CodeHighlight>
                            </Pii>
                            <Spacer height={2} />
                            <Banner>
                              <Heading size='sm'>Use the raw request to compute HMACs</Heading>
                              <Spacer height={1} />
                              <Paragraph size='md'>
                                In some languages, parsing the JSON may result in something that’s
                                not equivalent to the request body. For example, JavaScript may
                                round floats and reduce precision. We recommend using the raw
                                request body (found below) when computing the HMAC. Learn more about
                                webhook best practices{' '}
                                <a
                                  target='_blank'
                                  rel='noreferrer'
                                  href='https://docs.withpersona.com/docs/webhooks-best-practices'
                                >
                                  here
                                </a>
                                .
                              </Paragraph>
                            </Banner>
                          </Card.Body>
                        </Card>
                        <Card>
                          <Card.Body>
                            <Card.Title>Raw Request</Card.Title>
                            <Pii isRedacted={false}>
                              <CopyableField value={webhookEvent.attributes['body']} />
                            </Pii>
                          </Card.Body>
                        </Card>
                      </>
                    ),
                  },
                  {
                    name: 'Attempts',
                    path: '/attempts',
                    contents: (
                      <>
                        {webhookEvent.attributes.attempts.map((attempt, index) => (
                          <Card key={`attempt-${index}`}>
                            <Card.Body>
                              <Card.Title>Attempt #{index + 1}</Card.Title>
                              <InfoGrid>
                                <AttributeItem
                                  title='Status'
                                  value={
                                    <StickerContainer>
                                      <Sticker
                                        variant={
                                          attempt['response-status'] < 300 ? 'success' : 'danger'
                                        }
                                      >
                                        {attempt['response-status']}
                                      </Sticker>
                                    </StickerContainer>
                                  }
                                  width='100%'
                                />
                                <AttributeItem
                                  title='Response Headers'
                                  value={
                                    <Pii isRedacted={false}>
                                      <CodeHighlight
                                        id={`webhook-event-attempt__headers__${index}`}
                                        language='json'
                                        rowsToShow={40}
                                      >
                                        {JSON.stringify(attempt['response-headers'], null, 2)}
                                      </CodeHighlight>
                                    </Pii>
                                  }
                                  width='100%'
                                />
                                <AttributeItem
                                  title='Response Body'
                                  value={
                                    <Pii isRedacted={false}>
                                      <CodeHighlight
                                        id={`webhook-event-attempt__body__${index}`}
                                        rowsToShow={40}
                                      >
                                        {attempt['response-body']}
                                      </CodeHighlight>
                                    </Pii>
                                  }
                                  width='100%'
                                />
                                <AttributeItem
                                  title='Attempted at (UNIX timestamp)'
                                  value={<pre>{attempt['attempted-at-ts']}</pre>}
                                  width='100%'
                                />
                                <AttributeItem
                                  title='Signature(s)'
                                  value={attempt.signature}
                                  wordBreak='break-word'
                                  width='100%'
                                />
                                <AttributeItem
                                  title='Created at'
                                  value={DateTime.fromISO(attempt['created-at']).toFormat(
                                    DATE_AND_TIME_TZ_FORMAT,
                                  )}
                                  width='100%'
                                />
                              </InfoGrid>
                            </Card.Body>
                          </Card>
                        ))}
                        {webhookEvent.attributes.attempts.length < 1 && (
                          <Card key='attempt-no-attempts'>
                            <Card.Body>
                              <Card.Title>No Attempts</Card.Title>
                              <Paragraph>
                                No attempts were made. The Webhook Event either skipped or was{' '}
                                <a
                                  target='_blank'
                                  rel='noreferrer'
                                  href='https://docs.withpersona.com/docs/webhook-event-filters'
                                >
                                  filtered out.
                                </a>
                              </Paragraph>
                            </Card.Body>
                          </Card>
                        )}
                      </>
                    ),
                  },
                ]}
              />
            </MainColumn>
            <RightColumn>
              <Card>
                <Card.Body>
                  <Card.Title>Info</Card.Title>

                  <InfoGrid>
                    <AttributeItem
                      title='Status'
                      value={<WebhookEventStatusSticker webhookEvent={webhookEvent} />}
                      width='100%'
                    />

                    <AttributeItem
                      title='Webhook'
                      value={
                        <Link
                          to={`/dashboard/webhooks/${webhookEvent.relationships.webhook.data.id}`}
                        >
                          {webhookEvent.relationships.webhook.data.id}
                        </Link>
                      }
                      width='100%'
                    />

                    <AttributeItem
                      title='URL'
                      value={
                        <Link
                          to={`/dashboard/webhooks/${webhookEvent.relationships.webhook.data.id}`}
                        >
                          {webhookEvent.attributes['webhook-url']}
                        </Link>
                      }
                      width='100%'
                    />

                    <AttributeItem
                      title='Event name'
                      value={<EventNameSticker eventName={webhookEvent.attributes['event-name']} />}
                      width='100%'
                    />

                    <AttributeItem
                      title='Source Event'
                      value={
                        <Link to={`/dashboard/events/${webhookEvent.relationships.event.data.id}`}>
                          <MonoSpan>{webhookEvent.relationships.event.data.id}</MonoSpan>
                        </Link>
                      }
                      width='100%'
                    />

                    <AttributeItem
                      title='Attempts left'
                      value={webhookEvent.attributes['attempts-left']}
                      width='100%'
                    />

                    <AttributeItem
                      title='Created at'
                      value={DateTime.fromISO(webhookEvent.attributes['created-at']).toFormat(
                        DATE_AND_TIME_TZ_FORMAT,
                      )}
                      width='100%'
                    />

                    <AttributeItem
                      title='Last attempted at'
                      value={
                        webhookEvent.attributes['last-attempted-at'] &&
                        DateTime.fromISO(webhookEvent.attributes['last-attempted-at']).toFormat(
                          DATE_AND_TIME_TZ_FORMAT,
                        )
                      }
                      width='100%'
                    />

                    <AttributeItem
                      title='Next attempted at'
                      value={
                        webhookEvent.attributes['next-process-at'] &&
                        DateTime.fromISO(webhookEvent.attributes['next-process-at']).toFormat(
                          DATE_AND_TIME_TZ_FORMAT,
                        )
                      }
                      width='100%'
                    />
                  </InfoGrid>
                </Card.Body>
              </Card>
            </RightColumn>
          </ColumnContainer>
        </Container>
      </Backdrop>
    </>
  );
};

const StickerContainer = styled('div')`
  display: grid;
  grid-auto-columns: min-content;
  grid-auto-flow: column;

  grid-column-gap: ${Spacing[2]};
  grid-row-gap: ${Spacing[2]};
`;

const HeaderRow = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const Banner = styled.div`
  background-color: ${Color.BackgroundSecondary};
  padding: ${Spacing[4]};
`;

// // ==============================
// // Show Webhook Event Source
// // ==============================
// const handleShowWebhookEventSource = (webhookEvent: WebhookEvent) => {
//   history.push(`/dashboard/events/${webhookEvent.relationships.event.data.id}`);
// };

export default DashboardWebhookEventsShowView;
